"use client";

// Recommended posts — resource landing variant (Figma 8241:22520).
// Bordered white panel on a grey-50 band with a 3-col article grid and
// client-side pagination. Mirrors the press-releases section pattern.

import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Link } from "@/components/ui/link";
import { ControlledPagination } from "@/components/ui/pagination";
import { formatArticleCardDate } from "@/lib/content/article-helpers";
import { cn } from "@/lib/utils";

export interface RecommendedPostItem {
  id: string;
  title: string;
  slug: string;
  publishDate: string;
  thumbnail: { src: string; alt: string } | null;
  tag?: string;
}

interface RecommendedPostsSectionProps {
  title: string;
  viewAllLabel: string;
  items: RecommendedPostItem[];
  locale: string;
  className?: string;
}

const PAGE_SIZE = 3;

export function RecommendedPostsSection({
  title,
  viewAllLabel,
  items,
  locale,
  className,
}: RecommendedPostsSectionProps) {
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const start = (page - 1) * PAGE_SIZE;
  const visible = items.slice(start, start + PAGE_SIZE);

  return (
    <section
      data-testid="recommended-posts-section"
      className={cn(
        "bg-grey-50 md:border-grey-100 px-6 py-8 md:border-t md:px-8 md:py-0 md:pb-20 lg:px-20",
        className
      )}
    >
      <div className="md:border-grey-100 mx-auto w-full max-w-[1280px] md:border-x md:border-b md:bg-white md:p-10 lg:p-20">
        <div className="flex flex-col gap-5 md:gap-8 lg:gap-10">
          <header className="flex items-center justify-between gap-4">
            <h2 className="text-ds-heading-h3 md:text-ds-display-sm text-grey-950">
              {title}
            </h2>
            <Button
              size="sm"
              variant="secondary"
              render={<Link href="/blog" />}
            >
              {viewAllLabel}
            </Button>
          </header>

          <ul className="grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-2 md:gap-y-10 lg:grid-cols-3">
            {visible.map((item) => (
              <PostCard key={item.id} item={item} locale={locale} />
            ))}
          </ul>

          {totalPages > 1 ? (
            <ControlledPagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
              ariaLabel="Recommended posts pages"
              previousLabel="Previous page"
              nextLabel="Next page"
            />
          ) : null}
        </div>
      </div>
    </section>
  );
}

function PostCard({
  item,
  locale,
}: {
  item: RecommendedPostItem;
  locale: string;
}) {
  return (
    <li>
      <Link
        href={`/blog/${item.slug}`}
        className="group flex flex-col gap-6 no-underline"
      >
        <div className="rounded-ds-xl border-grey-100 bg-grey-100 aspect-[285/160] w-full overflow-hidden border">
          {item.thumbnail?.src ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={item.thumbnail.src}
              alt={item.thumbnail.alt}
              className="size-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.02]"
              loading="lazy"
              decoding="async"
            />
          ) : null}
        </div>
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-3">
            {item.tag ? (
              <span className="rounded-ds-s bg-grey-100 text-grey-950 inline-flex items-center px-1.5 py-1 text-[12px] leading-none font-medium">
                {item.tag}
              </span>
            ) : null}
            <time
              dateTime={item.publishDate}
              className="text-ds-body-sm text-grey-600"
            >
              {formatArticleCardDate(item.publishDate, locale)}
            </time>
          </div>
          <h3 className="text-ds-heading-h5 md:text-ds-body-lg text-grey-950 transition-opacity group-hover:opacity-80">
            {item.title}
          </h3>
        </div>
      </Link>
    </li>
  );
}
